import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, MetricCard, Text } from '@fragments';
import { IconAddressBook, IconArrowRight, IconBolt, IconInbox, IconMessageBolt, IconPlugConnected, IconPlus, IconRefresh, IconShieldCheck, IconSparkles, IconTemplate, IconUsers } from '@tabler/icons-react';
import { studioApi, idempotencyKey } from '../../../../data/studioApi';
import { STUDIO_AUTOMATIONS, STUDIO_AUTOMATIONS_CONNECT, STUDIO_INBOX, automationEditPath } from '../../../../enums/routes';
import { formatNumber } from '../../utils/format';
import AutomationsTabs from '../../components/AutomationsTabs';
import ConnectHero from '../../components/ConnectHero';
import Notice from '../../components/Notice';
import PageState from '../../components/PageState';
import TemplateCard from '../../components/TemplateCard';
import TemplatesDialog from '../../components/TemplatesDialog';
import './styles.scss';

const STEP_ICONS = { connect_account: IconPlugConnected, reconnect_account: IconPlugConnected, grant_permissions: IconShieldCheck, create_automation: IconPlus, use_template: IconTemplate, upgrade_plan: IconSparkles, contacts_limit_near: IconUsers };

// /studio/automations — where to start: the account behind the automations, what the plan still
// allows, ready-made templates and the next best actions (GET /automations/overview).
export default function Overview({ studio: s }) {
  const { t } = useTranslation('studioAutomations');
  const { overview, status, refresh } = s.automations;
  const profileId = s.profile.id;
  const [templates, setTemplates] = useState(null);
  const [picking, setPicking] = useState(false);
  const [creating, setCreating] = useState(null);
  const firstName = (s.profile.name || s.auth.user?.name || '').trim().split(/\s+/)[0];

  useEffect(() => {
    let alive = true;
    studioApi.automationTemplates({ profile_id: profileId }).then(page => { if (alive) setTemplates(page.data); }).catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
  }, [profileId]);

  const createFrom = async key => {
    setCreating(key);
    try {
      const automation = await studioApi.createAutomation({ profile_id: profileId, template_key: key }, idempotencyKey('automation'));
      refresh();
      s.navigate(automationEditPath(automation.id));
    } catch (failure) {
      setCreating(null);
      s.act(() => { throw failure; });
    }
  };
  // What each next step leads to; its text lives here, by `kind`.
  const stepAction = step => ({
    connect_account: () => s.navigate(STUDIO_AUTOMATIONS_CONNECT),
    reconnect_account: () => s.navigate(STUDIO_AUTOMATIONS_CONNECT),
    grant_permissions: () => s.navigate(STUDIO_AUTOMATIONS_CONNECT),
    create_automation: () => setPicking(true),
    use_template: () => createFrom(step.template_key),
    upgrade_plan: () => s.automations.upgrade(step.plan),
    contacts_limit_near: () => s.automations.upgrade(s.payments.nextPlan),
  }[step.kind]);

  if (status === 'error') return <PageState icon={<IconMessageBolt />} text={t('loadError')} action={<Button variant="rounded" color="overlay" startIcon={<IconRefresh />} preventLoading onClick={refresh}>{t('studio:retry')}</Button>} />;
  const connection = overview?.connection;
  const connected = overview?.connected_channels > 0;
  const reauthorize = connection?.status === 'reauthorization_required';
  const showHero = !!overview && !connected && !reauthorize;
  const missing = connected && connection.missing_capabilities.length > 0;
  const quota = overview?.quota;
  const contacts = overview?.contacts;
  const usage = !quota ? '' : !quota.available ? t('usageNeedsPlan') : quota.limit === null ? t('usageUnlimited', { count: quota.active }) : t('usageOf', { count: quota.active, limit: quota.limit });
  const contactUsage = !contacts ? '' : contacts.limit === null ? formatNumber(contacts.total, s.locale) : t('usageOf', { count: formatNumber(contacts.total, s.locale), limit: formatNumber(contacts.limit, s.locale) });
  const recommended = (templates || []).filter(item => item.recommended);
  const titleOf = key => templates?.find(item => item.key === key)?.title;
  // What is already on this page, above, is not repeated as a next step.
  const nextSteps = (overview?.next_steps || []).filter(step => !(step.kind === 'connect_account' && showHero) && !(step.kind === 'reconnect_account' && reauthorize) && !(step.kind === 'grant_permissions' && missing)
    && !(step.kind === 'use_template' && recommended.some(item => item.key === step.template_key)));

  return <div className="studio-automations__page studio-automation-home">
    <header className="studio-automations__head">
      <div className="studio-automations__heading">
        <Text.caption contrast={6} left={<IconMessageBolt />}>{t('title')}</Text.caption>
        <Text.h2 weight={400} contrast={9}>{firstName ? t('homeGreeting', { name: firstName }) : t('title')}</Text.h2>
        <Text.p contrast={7} loading={!overview}>{!overview ? t('homeSubtitle') : connected ? t('homeConnected', { count: overview.connected_channels, handle: connection.handle }) : t('homeSubtitle')}</Text.p>
      </div>
      <AutomationsTabs studio={s} value={STUDIO_AUTOMATIONS} />
    </header>

    {reauthorize && <Notice title={t('noticeReauthTitle')} text={t('noticeReauthText')} action={<Button size="small" variant="rounded" color="inverse" preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS_CONNECT)}>{t('reauthorizeAction')}</Button>} />}
    {missing && <Notice title={t('noticeMissingTitle')} text={t('noticeMissingText')} action={<Button size="small" variant="rounded" color="inverse" preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS_CONNECT)}>{t('connectGrant')}</Button>} />}

    {showHero && <ConnectHero studio={s} variant="connect" embedded />}

    {connected && <section className="studio-automations__grid" aria-label={t('homeUsage')}>
      <MetricCard title={t('usageActive')} leftIcon={<IconBolt />} value={usage} description={t('usageActiveHint')} />
      <MetricCard title={t('usageContacts')} leftIcon={<IconAddressBook />} value={contactUsage} description={t('usageContactsHint')} />
      <button type="button" className="studio-automation-home__tile" onClick={() => s.navigate(STUDIO_INBOX)}>
        <MetricCard title={t('usageInbox')} leftIcon={<IconInbox />} value={formatNumber(overview.inbox.unread_conversations, s.locale)} rightIcon={<IconArrowRight />} />
      </button>
    </section>}

    <section className="studio-automations__section">
      <div className="studio-automations__section-head">
        <Text.h5 weight={500} contrast={9}>{t('homeStart')}</Text.h5>
        <Button variant="text" color="overlay" size="small" preventLoading onClick={() => setPicking(true)}>{t('homeAllTemplates')}</Button>
      </div>
      <div className="studio-automations__grid">
        {templates ? recommended.map((template, index) => <TemplateCard key={template.key} studio={s} template={template} index={index} busy={creating === template.key} onPick={item => createFrom(item.key)} />)
          : [0, 1, 2].map(key => <Card key={key} flat><Text.label loading>{t('templatesTitle')}</Text.label><Text.span loading>{t('templatesSubtitle')}</Text.span></Card>)}
      </div>
    </section>

    {nextSteps.length > 0 && <section className="studio-automations__section">
      <Text.h5 weight={500} contrast={9}>{t('homeNext')}</Text.h5>
      <div className="studio-automations__grid studio-automations__grid--two">
        {nextSteps.map((step, index) => {
          const Glyph = STEP_ICONS[step.kind] || IconArrowRight;
          const planName = step.plan ? t(`studio:planName_${step.plan}`) : '';
          const go = stepAction(step);
          const title = step.kind === 'use_template' ? titleOf(step.template_key) || t('step_use_template') : t(`step_${step.kind}`, { plan: planName });
          return <Card key={`${step.kind}-${step.template_key || step.plan || index}`} interactive flat className="studio-automation-step studio-automations__rise" style={{ '--rise-index': index }} onClick={go}>
            <span className={`studio-automation-step__icon${step.kind === 'upgrade_plan' ? ' is-upgrade' : ''}`} aria-hidden="true"><Glyph /></span>
            <span className="studio-automation-step__text">
              <Text.label contrast={9}>{title}</Text.label>
              <Text.span contrast={6}>{t(`step_${step.kind}_hint`, { plan: planName })}</Text.span>
            </span>
            <IconArrowRight className="studio-automation-step__go" />
          </Card>;
        })}
      </div>
    </section>}

    {picking && <TemplatesDialog studio={s} onClose={() => setPicking(false)} />}
  </div>;
}
